import { useState } from 'react'
import { Plus, Search, Pencil, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Switch } from '@/shared/components/ui/switch'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/shared/components/ui/table'
import {
  useConfiguraciones, useCreateConfiguracion, useUpdateConfiguracion, useDeleteConfiguracion,
  useLaboratorios, useMarcas,
} from '@/modules/multichannel/hooks/useConfiguracionCampanias'
import { ConfiguracionFormDialog } from './ConfiguracionFormDialog'
import type { ConfigCampania } from '@/modules/multichannel/types/configuracion'

export function ConfiguracionesTab() {
  const [search, setSearch] = useState('')
  const [labFilter, setLabFilter] = useState<number | undefined>()
  const [marcaFilter, setMarcaFilter] = useState<number | undefined>()
  const [showInactive, setShowInactive] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<ConfigCampania | null>(null)

  const { data: laboratorios } = useLaboratorios({ activo: true })
  const { data: allMarcas } = useMarcas(labFilter ? { marca_principal_id: labFilter, activo: true } : { activo: true })
  const { data: configuraciones, isLoading } = useConfiguraciones({
    marca_secundaria_id: marcaFilter,
    ...(showInactive ? {} : { activo: true }),
  })
  // Todas las marcas para el dialog
  const { data: allMarcasForDialog } = useMarcas({ activo: true })

  const createMutation = useCreateConfiguracion()
  const updateMutation = useUpdateConfiguracion()
  const deleteMutation = useDeleteConfiguracion()

  const filtered = (configuraciones || []).filter(c => {
    if (!search) return true
    const q = search.toLowerCase()
    return c.nombre_campania.toLowerCase().includes(q) ||
      c.nombre_encargado.toLowerCase().includes(q) ||
      c.target.toLowerCase().includes(q)
  })

  const handleLabChange = (val: string) => {
    setLabFilter(val ? Number(val) : undefined)
    setMarcaFilter(undefined)
  }

  return (
    <div className="space-y-4">
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              Configuraciones de Campana
              {configuraciones && <Badge className="badge-gradient text-xs">{filtered.length}</Badge>}
            </CardTitle>
            <Button onClick={() => { setEditing(null); setDialogOpen(true) }} size="sm" className="bg-teal-800 hover:bg-teal-900 text-white">
              <Plus className="mr-2 h-4 w-4" /> Nueva
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <select
              value={labFilter || ''}
              onChange={e => handleLabChange(e.target.value)}
              className="flex h-9 w-full md:w-48 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">Todos los labs</option>
              {(laboratorios || []).map(l => (
                <option key={l.id} value={l.id}>{l.nombre}</option>
              ))}
            </select>
            <select
              value={marcaFilter || ''}
              onChange={e => setMarcaFilter(e.target.value ? Number(e.target.value) : undefined)}
              disabled={!labFilter}
              className="flex h-9 w-full md:w-48 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
            >
              <option value="">Todas las marcas</option>
              {(allMarcas || []).map(m => (
                <option key={m.id} value={m.id}>{m.nombre}</option>
              ))}
            </select>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar nombre, encargado, target..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Button variant={showInactive ? 'default' : 'outline'} size="sm" onClick={() => setShowInactive(!showInactive)}
              className={showInactive ? 'bg-teal-800 hover:bg-teal-900 text-white' : ''}>
              {showInactive ? 'Solo activos' : 'Mostrar todos'}
            </Button>
          </div>

          {isLoading ? (
            <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : filtered.length > 0 ? (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                  <TableRow>
                    <TableHead>Marca</TableHead>
                    <TableHead>Nombre Campana</TableHead>
                    <TableHead>Encargado</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(config => (
                    <TableRow key={config.id}>
                      <TableCell>{config.marca_secundaria_nombre}</TableCell>
                      <TableCell className="font-medium">{config.nombre_campania}</TableCell>
                      <TableCell>{config.nombre_encargado}</TableCell>
                      <TableCell>{config.target}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch checked={config.activo}
                            onCheckedChange={() => updateMutation.mutate({ id: config.id, data: { activo: !config.activo } })}
                            className={config.activo ? 'data-[state=checked]:bg-teal-500' : ''} />
                          <Badge className={config.activo ? 'badge-gradient shadow-none' : 'bg-gray-100 text-gray-500 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 shadow-none'}>
                            {config.activo ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setEditing(config); setDialogOpen(true) }}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={async () => {
                            if (window.confirm(`Eliminar "${config.nombre_campania}"?`)) await deleteMutation.mutateAsync(config.id)
                          }}><Trash2 className="h-4 w-4 text-gray-600" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              {search || marcaFilter || labFilter ? 'No se encontraron configuraciones.' : 'No hay configuraciones creadas.'}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfiguracionFormDialog
        open={dialogOpen}
        onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditing(null) }}
        configuracion={editing}
        marcas={allMarcasForDialog || []}
        onSubmit={async (data) => { await createMutation.mutateAsync(data); setDialogOpen(false) }}
        onUpdate={async (id, data) => { await updateMutation.mutateAsync({ id, data }); setDialogOpen(false) }}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  )
}
